import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";
import {
  getAttendance,
  getEmployees,
  saveAttendance,
  getTimesheets,
  createTimesheet,
  updateTimesheet,
  getPeriods,
} from "../payrollService";

function pickOne(value) {
  if (!value) return null;
  return Array.isArray(value) ? value[0] || null : value;
}

const initialAttendanceForm = {
  employee_id: "",
  attendance_date: "",
  worked_hours: "",
  overtime_hours: "",
  status: "present",
  notes: "",
};

const initialTimesheetForm = {
  employee_id: "",
  period_start: "",
  period_end: "",
  regular_hours: "",
  overtime_hours: "",
  holiday_hours: "",
  unpaid_hours: "",
  notes: "",
};

export default function PayrollAttendance() {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState("");

  const [employees, setEmployees] = useState([]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [timesheetRows, setTimesheetRows] = useState([]);
  const [periods, setPeriods] = useState([]);

  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [showTimesheetModal, setShowTimesheetModal] = useState(false);

  const [attendanceForm, setAttendanceForm] = useState(initialAttendanceForm);
  const [timesheetForm, setTimesheetForm] = useState(initialTimesheetForm);

  const [editingTimesheetId, setEditingTimesheetId] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setMsg("");

    try {
      const [employeeData, attendanceData, timesheetData, periodData] =
        await Promise.all([
          getEmployees(companyId),
          getAttendance(companyId),
          getTimesheets(companyId),
          getPeriods(companyId),
        ]);

      setEmployees(employeeData || []);
      setAttendanceRows(attendanceData || []);
      setTimesheetRows(timesheetData || []);
      setPeriods(periodData || []);
    } catch (err) {
      setMsg(err.message || "Failed to load attendance data");
    } finally {
      setBusy(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const presentCount = useMemo(
    () => attendanceRows.filter((x) => x.status === "present").length,
    [attendanceRows]
  );

  const lateCount = useMemo(
    () => attendanceRows.filter((x) => x.status === "late").length,
    [attendanceRows]
  );

  const totalOvertime = useMemo(
    () =>
      attendanceRows.reduce(
        (sum, row) => sum + Number(row.overtime_hours || 0),
        0
      ),
    [attendanceRows]
  );

  async function handleSaveAttendance(e) {
    e.preventDefault();

    try {
      setMsg("");

      await saveAttendance({
        company_id: companyId,
        employee_id: attendanceForm.employee_id,
        attendance_date: attendanceForm.attendance_date,
        worked_hours: Number(attendanceForm.worked_hours || 0),
        overtime_hours: Number(attendanceForm.overtime_hours || 0),
        status: attendanceForm.status,
        notes: attendanceForm.notes || null,
      });

      setAttendanceForm(initialAttendanceForm);
      setShowAttendanceModal(false);
      await load();
      setMsg("Attendance saved successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to save attendance");
    }
  }

  async function handleSaveTimesheet(e) {
    e.preventDefault();

    try {
      setMsg("");

      const payload = {
        company_id: companyId,
        employee_id: timesheetForm.employee_id,
        period_start: timesheetForm.period_start,
        period_end: timesheetForm.period_end,
        regular_hours: Number(timesheetForm.regular_hours || 0),
        overtime_hours: Number(timesheetForm.overtime_hours || 0),
        holiday_hours: Number(timesheetForm.holiday_hours || 0),
        unpaid_hours: Number(timesheetForm.unpaid_hours || 0),
        notes: timesheetForm.notes || null,
      };

      if (editingTimesheetId) {
        await updateTimesheet(editingTimesheetId, payload);
      } else {
        await createTimesheet(payload);
      }

      setTimesheetForm(initialTimesheetForm);
      setEditingTimesheetId("");
      setShowTimesheetModal(false);
      await load();
      setMsg(
        `Timesheet ${editingTimesheetId ? "updated" : "created"} successfully.`
      );
    } catch (err) {
      setMsg(err.message || "Failed to save timesheet");
    }
  }

  function handleEditTimesheet(row) {
    setEditingTimesheetId(row.id);
    setTimesheetForm({
      employee_id: row.employee_id || "",
      period_start: row.period_start || "",
      period_end: row.period_end || "",
      regular_hours: row.regular_hours ?? "",
      overtime_hours: row.overtime_hours ?? "",
      holiday_hours: row.holiday_hours ?? "",
      unpaid_hours: row.unpaid_hours ?? "",
      notes: row.notes || "",
    });
    setShowTimesheetModal(true);
  }

  function applyPeriodToTimesheet(periodId) {
    const found = periods.find((p) => p.id === periodId);
    if (!found) return;

    setTimesheetForm((prev) => ({
      ...prev,
      period_start: found.period_start || "",
      period_end: found.period_end || "",
    }));
  }

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert info">{msg}</div> : null}

      <div className="payroll-grid payroll-grid-4">
        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Attendance Records</p>
          <h3>{attendanceRows.length}</h3>
          <span>Total logged records</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Present</p>
          <h3>{presentCount}</h3>
          <span>Employees marked present</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Late</p>
          <h3>{lateCount}</h3>
          <span>Late attendance entries</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Overtime Hours</p>
          <h3>{Number(totalOvertime).toLocaleString()}</h3>
          <span>Total overtime logged</span>
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Attendance & Timesheets</h3>
            <p>Track daily attendance and maintain payroll timesheets.</p>
          </div>

          <div className="table-actions">
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setShowAttendanceModal(true)}
            >
              <Plus size={16} />
              Add Attendance
            </button>

            <button
              type="button"
              className="primary-btn"
              onClick={() => {
                setEditingTimesheetId("");
                setTimesheetForm(initialTimesheetForm);
                setShowTimesheetModal(true);
              }}
            >
              <Plus size={16} />
              Add Timesheet
            </button>
          </div>
        </div>

        <div className="payroll-grid payroll-grid-2">
          <div className="payroll-card">
            <div className="payroll-card-header">
              <h3>Attendance Log</h3>
            </div>

            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Worked Hours</th>
                    <th>Overtime</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceRows.map((row) => {
                    const employee = pickOne(row.payroll_employees);
                    return (
                      <tr key={row.id}>
                        <td>
                          {employee
                            ? `${employee.employee_code} - ${employee.first_name} ${employee.last_name}`
                            : "-"}
                        </td>
                        <td>{row.attendance_date}</td>
                        <td>
                          <span className={`pill ${row.status}`}>
                            {row.status}
                          </span>
                        </td>
                        <td>
                          {Number(row.worked_hours || 0).toLocaleString()}
                        </td>
                        <td>
                          {Number(row.overtime_hours || 0).toLocaleString()}
                        </td>
                        <td>{row.notes || "-"}</td>
                      </tr>
                    );
                  })}

                  {!attendanceRows.length && !busy && (
                    <tr>
                      <td colSpan="6">No attendance records found.</td>
                    </tr>
                  )}

                  {busy && (
                    <tr>
                      <td colSpan="6">Loading...</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="payroll-card">
            <div className="payroll-card-header">
              <h3>Timesheets</h3>
            </div>

            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Period</th>
                    <th>Regular Hours</th>
                    <th>Overtime</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {timesheetRows.map((row) => {
                    const employee = pickOne(row.payroll_employees);
                    return (
                      <tr key={row.id}>
                        <td>
                          {employee
                            ? `${employee.employee_code} - ${employee.first_name} ${employee.last_name}`
                            : "-"}
                        </td>
                        <td>
                          {row.period_start} - {row.period_end}
                        </td>
                        <td>
                          {Number(row.regular_hours || 0).toLocaleString()}
                        </td>
                        <td>
                          {Number(row.overtime_hours || 0).toLocaleString()}
                        </td>
                        <td>
                          <span className={`pill ${row.status}`}>
                            {row.status}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="ghost-btn"
                            onClick={() => handleEditTimesheet(row)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                  {!timesheetRows.length && !busy && (
                    <tr>
                      <td colSpan="6">No timesheets found.</td>
                    </tr>
                  )}

                  {busy && (
                    <tr>
                      <td colSpan="6">Loading...</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showAttendanceModal ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <h3>Add Attendance</h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowAttendanceModal(false)}
              >
                Close
              </button>
            </div>

            <form className="payroll-form" onSubmit={handleSaveAttendance}>
              <div className="payroll-grid payroll-grid-2">
                <label>
                  Employee
                  <select
                    value={attendanceForm.employee_id}
                    onChange={(e) =>
                      setAttendanceForm((p) => ({
                        ...p,
                        employee_id: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="">Select employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.employee_code} - {emp.first_name} {emp.last_name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Attendance Date
                  <input
                    type="date"
                    value={attendanceForm.attendance_date}
                    onChange={(e) =>
                      setAttendanceForm((p) => ({
                        ...p,
                        attendance_date: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Worked Hours
                  <input
                    type="number"
                    step="0.01"
                    value={attendanceForm.worked_hours}
                    onChange={(e) =>
                      setAttendanceForm((p) => ({
                        ...p,
                        worked_hours: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Overtime Hours
                  <input
                    type="number"
                    step="0.01"
                    value={attendanceForm.overtime_hours}
                    onChange={(e) =>
                      setAttendanceForm((p) => ({
                        ...p,
                        overtime_hours: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Status
                  <select
                    value={attendanceForm.status}
                    onChange={(e) =>
                      setAttendanceForm((p) => ({
                        ...p,
                        status: e.target.value,
                      }))
                    }
                  >
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="late">Late</option>
                    <option value="half_day">Half Day</option>
                    <option value="holiday">Holiday</option>
                    <option value="weekend">Weekend</option>
                    <option value="leave">Leave</option>
                  </select>
                </label>
              </div>

              <label>
                Notes
                <textarea
                  rows="4"
                  value={attendanceForm.notes}
                  onChange={(e) =>
                    setAttendanceForm((p) => ({ ...p, notes: e.target.value }))
                  }
                />
              </label>

              <div className="payroll-form-actions">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => setShowAttendanceModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-btn">
                  Save Attendance
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showTimesheetModal ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <h3>{editingTimesheetId ? "Edit Timesheet" : "Add Timesheet"}</h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => {
                  setShowTimesheetModal(false);
                  setEditingTimesheetId("");
                }}
              >
                Close
              </button>
            </div>

            <form className="payroll-form" onSubmit={handleSaveTimesheet}>
              <div className="payroll-grid payroll-grid-2">
                <label>
                  Employee
                  <select
                    value={timesheetForm.employee_id}
                    onChange={(e) =>
                      setTimesheetForm((p) => ({
                        ...p,
                        employee_id: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="">Select employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.employee_code} - {emp.first_name} {emp.last_name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Use Existing Payroll Period
                  <select
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value)
                        applyPeriodToTimesheet(e.target.value);
                    }}
                  >
                    <option value="">Select period</option>
                    {periods.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.period_name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Period Start
                  <input
                    type="date"
                    value={timesheetForm.period_start}
                    onChange={(e) =>
                      setTimesheetForm((p) => ({
                        ...p,
                        period_start: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Period End
                  <input
                    type="date"
                    value={timesheetForm.period_end}
                    onChange={(e) =>
                      setTimesheetForm((p) => ({
                        ...p,
                        period_end: e.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label>
                  Regular Hours
                  <input
                    type="number"
                    step="0.01"
                    value={timesheetForm.regular_hours}
                    onChange={(e) =>
                      setTimesheetForm((p) => ({
                        ...p,
                        regular_hours: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Overtime Hours
                  <input
                    type="number"
                    step="0.01"
                    value={timesheetForm.overtime_hours}
                    onChange={(e) =>
                      setTimesheetForm((p) => ({
                        ...p,
                        overtime_hours: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Holiday Hours
                  <input
                    type="number"
                    step="0.01"
                    value={timesheetForm.holiday_hours}
                    onChange={(e) =>
                      setTimesheetForm((p) => ({
                        ...p,
                        holiday_hours: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Unpaid Hours
                  <input
                    type="number"
                    step="0.01"
                    value={timesheetForm.unpaid_hours}
                    onChange={(e) =>
                      setTimesheetForm((p) => ({
                        ...p,
                        unpaid_hours: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>

              <label>
                Notes
                <textarea
                  rows="4"
                  value={timesheetForm.notes}
                  onChange={(e) =>
                    setTimesheetForm((p) => ({ ...p, notes: e.target.value }))
                  }
                />
              </label>

              <div className="payroll-form-actions">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => {
                    setShowTimesheetModal(false);
                    setEditingTimesheetId("");
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-btn">
                  {editingTimesheetId ? "Update Timesheet" : "Save Timesheet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
